import React, { useState, useEffect, useId, useMemo, useCallback } from "react";
import { Sparkles, Check, Edit3, X, Compass, ArrowRight, ShieldAlert, Info, CheckCircle2, AlertCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useBridgeStore, bridgeActions, ASPIRATIONS, MAX_ASPIRATIONS, USER_INTERPRETATION_MAX, INTERPRETATION_DISCLAIMER, buildInterpretation, type AspirationId, type PersonaChoice, type BridgePersona } from "@/lib/dreamBridge";
import "./bridge-sections.css";
export default function DiscoverWhatMatters() {
  const rewordInputId = useId();

  // Store subscriptions (returning stable references only)
  const personas = useBridgeStore(s => s.personas);
  const bridge = useBridgeStore(s => s.bridge);
  const {
    personaChoice,
    themes,
    aspirations,
    interpretationDecision,
    rewordedText
  } = bridge;

  // Local draft state for the reword text area
  const [isRewording, setIsRewording] = useState(false);
  const [localRewordDraft, setLocalRewordDraft] = useState(rewordedText || "");

  // Sync draft when store rewordedText changes from external/saved state
  useEffect(() => {
    if (rewordedText) {
      setLocalRewordDraft(rewordedText);
    }
  }, [rewordedText]);

  // Resolve current active BridgePersona from choice + available personas
  const currentPersona: BridgePersona | null = useMemo(() => {
    if (!personaChoice) return null;
    if (personaChoice.kind === "own") {
      return {
        kind: "own"
      };
    }
    const found = personas.find(p => p.index === personaChoice.index);
    if (found) {
      return {
        kind: "self",
        archetype: found.archetype,
        name: found.name,
        title: found.title,
        occupation: found.occupation,
        scenarioLabel: found.scenarioLabel,
        topAxis: found.topAxis
      };
    }
    // Chosen index no longer exists in revealed personas
    return null;
  }, [personaChoice, personas]);

  // Persona selection handler
  const handleSelectPersona = useCallback((choice: PersonaChoice) => {
    bridgeActions.setPersonaChoice(choice);
    // Changing persona resets any existing decision to pending
    if (interpretationDecision !== "pending") {
      bridgeActions.decideInterpretation("pending");
    }
    setIsRewording(false);
  }, [interpretationDecision]);

  // Aspiration selection handler
  const handleToggleAspiration = useCallback((aspirationId: AspirationId) => {
    bridgeActions.toggleAspiration(aspirationId);
    setIsRewording(false);
  }, []);

  // Compute interpretation dynamically when persona + aspirations are active
  const computedInterpretation = useMemo(() => {
    if (!currentPersona || aspirations.length === 0) {
      return null;
    }
    return buildInterpretation({
      persona: currentPersona,
      themes,
      aspirations
    });
  }, [currentPersona, themes, aspirations]);

  // Interpretation decision handlers
  const handleAccept = useCallback(() => {
    bridgeActions.decideInterpretation("accepted");
    setIsRewording(false);
  }, []);
  const handleStartReword = useCallback(() => {
    setLocalRewordDraft(rewordedText || computedInterpretation?.text || "");
    setIsRewording(true);
  }, [rewordedText, computedInterpretation]);
  const handleSaveReword = useCallback(() => {
    const trimmed = localRewordDraft.trim();
    if (!trimmed) return;
    bridgeActions.decideInterpretation("reworded", trimmed);
    setIsRewording(false);
  }, [localRewordDraft]);
  const handleReject = useCallback(() => {
    bridgeActions.decideInterpretation("rejected");
    setIsRewording(false);
  }, []);
  const isRewordSaveDisabled = localRewordDraft.trim().length === 0;
  return <section id="bridge-matters" aria-labelledby="bridge-matters-title" tabIndex={-1} className="dtr-section ql-glass-strong rounded-2xl p-4 sm:p-6 md:p-10 border-primary/30 shadow-2xl space-y-8 outline-none">
      {/* Step Header */}
      <div className="space-y-2 border-b border-border/60 pb-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="ql-eyebrow">Step 2 of 4</span>
          <span className="font-mono text-xs text-muted-foreground">
            Values & Horizon Translation
          </span>
        </div>
        <h3 id="bridge-matters-title" className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-foreground break-words">
          Discover What Truly Matters
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-3xl">
          Connect your imagined identity to tangible life values. Select which persona to focus on, choose the real-world areas you want to enrich, and review a tentative reading of what this inner pull might be asking for.
        </p>
      </div>

      {/* Part A: Choose Who to Focus On */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-foreground">
              A. Which Imagined Persona Do You Want to Bridge?
            </h4>
            <p className="text-xs text-muted-foreground">
              Select one of your four revealed multiverse selves, or focus on your own custom daydreamed life.
            </p>
          </div>
          {personaChoice && <span className="font-mono text-xs text-primary font-semibold">
              {personaChoice.kind === "self" ? `Self #${personaChoice.index + 1} chosen` : "Own daydream chosen"}
            </span>}
        </div>

        {/* Personas grid */}
        <div role="radiogroup" aria-label="Persona Choice" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-1">
          {/* Revealed Selves */}
          {personas.length > 0 ? personas.map(self => {
          const isSelected = personaChoice?.kind === "self" && personaChoice.index === self.index;
          return <div key={self.index} role="radio" aria-checked={isSelected} tabIndex={0} onClick={() => handleSelectPersona({
            kind: "self",
            index: self.index
          })} onKeyDown={e => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              handleSelectPersona({
                kind: "self",
                index: self.index
              });
            }
          }} data-selected={isSelected} className="dtr-persona-card flex flex-col justify-between min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                  <div className="space-y-2 min-w-0">
                    <div className="flex items-center justify-between gap-1.5 min-w-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-primary/10 text-primary border border-primary/30 shrink-0">
                        Fictional
                      </span>
                      <div aria-hidden="true" className={`size-4 rounded-full border flex items-center justify-center shrink-0 ${isSelected ? "border-primary bg-primary" : "border-border bg-transparent"}`}>
                        {isSelected && <span className="size-1.5 rounded-full bg-primary-foreground" />}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <h5 className="font-serif text-sm font-bold text-foreground break-words">
                        {self.name}
                      </h5>
                      <p className="text-[11px] text-primary/90 font-mono break-words">
                        {self.occupation}
                      </p>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-snug break-words">
                      &ldquo;{self.scenarioLabel}&rdquo;
                    </p>
                  </div>
                  <div className="pt-2 text-[10px] font-mono text-muted-foreground border-t border-border/40 mt-2 break-words">
                    {self.universeName}
                  </div>
                </div>;
        }) : <div className="sm:col-span-2 lg:col-span-4 p-4 rounded-xl border border-border/60 bg-card/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted-foreground min-w-0">
              <div className="flex items-start gap-2.5 min-w-0">
                <Info className="size-4 text-primary shrink-0 mt-0.5" />
                <span className="min-w-0 break-words">
                  No multiverse selves generated yet. You can bridge your own imagined life right now, or generate four alternate selves above.
                </span>
              </div>
              <Link to="#selves-studio" className="ql-btn-ghost text-xs min-h-[44px] shrink-0 font-serif">
                <span>Reveal selves first &uarr;</span>
              </Link>
            </div>}

          {/* Option: My Own Imagined Life */}
          <div role="radio" aria-checked={personaChoice?.kind === "own"} tabIndex={0} onClick={() => handleSelectPersona({
          kind: "own"
        })} onKeyDown={e => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            handleSelectPersona({
              kind: "own"
            });
          }
        }} data-selected={personaChoice?.kind === "own"} className="dtr-persona-card flex flex-col justify-between min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <div className="space-y-2 min-w-0">
              <div className="flex items-center justify-between gap-1.5 min-w-0">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-chart-4/15 text-chart-4 border border-chart-4/30 shrink-0">
                  Private
                </span>
                <div aria-hidden="true" className={`size-4 rounded-full border flex items-center justify-center shrink-0 ${personaChoice?.kind === "own" ? "border-primary bg-primary" : "border-border bg-transparent"}`}>
                  {personaChoice?.kind === "own" && <span className="size-1.5 rounded-full bg-primary-foreground" />}
                </div>
              </div>
              <div className="min-w-0">
                <h5 className="font-serif text-sm font-bold text-foreground break-words">
                  My Own Imagined Life
                </h5>
                <p className="text-[11px] text-muted-foreground font-mono break-words">
                  Personal daydream
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug break-words">
                Your private imagined world, values, or secret creative ambition.
              </p>
            </div>
            <div className="pt-2 text-[10px] font-mono text-muted-foreground border-t border-border/40 mt-2 break-words">
              Custom inner sandbox
            </div>
          </div>
        </div>
      </div>

      {/* Part B: Real-World Aspirations */}
      <div className="space-y-3.5 pt-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-foreground">
              B. Where in the Real World Could That Energy Anchor?
            </h4>
            <p className="text-xs text-muted-foreground">
              Select up to {MAX_ASPIRATIONS} practical life areas where you want to experience that feeling.
            </p>
          </div>
          <span className="self-start sm:self-auto font-mono text-xs text-primary font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/30 shadow-[0_0_8px_var(--glow-primary)]">
            {aspirations.length} / {MAX_ASPIRATIONS} selected
          </span>
        </div>

        <div role="group" aria-label="Real-World Aspirations" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-1">
          {ASPIRATIONS.map(item => {
          const isSelected = aspirations.includes(item.id as AspirationId);
          const isMaxReached = aspirations.length >= MAX_ASPIRATIONS && !isSelected;
          return <button key={item.id} type="button" role="checkbox" aria-checked={isSelected} disabled={isMaxReached} onClick={() => handleToggleAspiration(item.id as AspirationId)} data-active={isSelected} className={`ql-card p-3.5 text-left flex flex-col justify-between min-h-[92px] min-w-0 cursor-pointer transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${isSelected ? "border-primary bg-primary/10 ring-1 ring-primary/40 shadow-[0_0_16px_var(--glow-primary)]" : isMaxReached ? "opacity-40 cursor-not-allowed" : "hover:border-primary/50"}`}>
                <div className="flex items-start justify-between gap-1.5 min-w-0">
                  <span className="font-serif text-xs sm:text-sm font-bold text-foreground leading-snug min-w-0 break-words">
                    {item.label}
                  </span>
                  <div aria-hidden="true" className={`size-4 rounded-full flex items-center justify-center shrink-0 border transition-colors ${isSelected ? "bg-primary text-primary-foreground border-primary" : "border-border/80 bg-background/50"}`}>
                    {isSelected && <Check className="size-2.5 stroke-[3]" />}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed min-w-0 break-words line-clamp-2">
                  {item.hint}
                </p>
              </button>;
        })}
        </div>
      </div>

      {/* Part C: Tentative Interpretation Card */}
      <div className="space-y-4 pt-4 border-t border-border/60">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-foreground">
              C. A Possible Reading — Not a Verdict
            </h4>
            <p className="text-xs text-muted-foreground">
              A tentative hypothesis of what this inner pull might be saying. You are always the authority on your own mind.
            </p>
          </div>
          {interpretationDecision !== "pending" && <span className={`font-mono text-xs px-2.5 py-1 rounded-full uppercase font-bold tracking-wider ${interpretationDecision === "accepted" ? "bg-primary/20 text-primary border border-primary/40" : interpretationDecision === "reworded" ? "bg-chart-4/20 text-chart-4 border border-chart-4/40" : "bg-destructive/20 text-destructive border border-destructive/40"}`}>
              Status: {interpretationDecision}
            </span>}
        </div>

        {/* State 1: Incomplete selections */}
        {(!currentPersona || aspirations.length === 0) && <div className="p-6 rounded-xl border border-dashed border-border/80 bg-card/40 text-center space-y-2">
            <Compass className="size-6 text-primary/70 mx-auto" />
            <p className="text-xs sm:text-sm text-foreground font-semibold">
              Select a persona and at least one real-world aspiration above
            </p>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              Once both are selected, we will synthesize a tentative reading of the values at play.
            </p>
          </div>}

        {/* State 2: Active Interpretation */}
        {currentPersona && aspirations.length > 0 && computedInterpretation && <div className="space-y-5">
            {/* If visitor rejected the reading */}
            {interpretationDecision === "rejected" ? <div className="p-5 sm:p-6 rounded-xl border border-border/80 bg-card/60 space-y-3">
                <div className="flex items-center gap-2 text-primary font-serif font-bold text-sm">
                  <CheckCircle2 className="size-4" />
                  <span>Thanks for letting us know</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Nothing from that reading will be used in your action plan. Feel free to choose different real-world aspirations above, or use <strong>Reword it</strong> below to write your own interpretation in your own words.
                </p>
                <div className="pt-1">
                  <button type="button" onClick={handleStartReword} className="ql-btn min-h-[44px] text-xs font-serif">
                    <Edit3 className="size-3.5 mr-1.5" />
                    <span>Reword it in your own words</span>
                  </button>
                </div>
              </div> : (/* Normal interpretation display or reword form */
        <div className="p-5 sm:p-8 rounded-xl border border-primary/40 bg-card/85 backdrop-blur-md shadow-xl space-y-5">
                {/* Interpretation Text or Reword Editor */}
                {isRewording ? <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <label htmlFor={rewordInputId} className="text-xs font-bold text-foreground font-mono flex items-center gap-1.5">
                        <Edit3 className="size-3.5 text-primary" />
                        <span>Reword This Interpretation In Your Own Words:</span>
                      </label>
                      <span className="dtr-counter text-muted-foreground">
                        {localRewordDraft.length} / {USER_INTERPRETATION_MAX}
                      </span>
                    </div>
                    <textarea id={rewordInputId} rows={4} maxLength={USER_INTERPRETATION_MAX} value={localRewordDraft} onChange={e => setLocalRewordDraft(e.target.value)} placeholder="State what this imagined self or desire means to you in your own words..." className="ql-input text-base sm:text-sm min-h-[100px] resize-y" />
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <button type="button" disabled={isRewordSaveDisabled} onClick={handleSaveReword} className="ql-btn min-h-[44px] text-xs font-serif">
                        <Check className="size-3.5 mr-1.5" />
                        <span>Save reworded interpretation</span>
                      </button>
                      <button type="button" onClick={() => setIsRewording(false)} className="ql-btn-ghost min-h-[44px] text-xs font-serif">
                        <X className="size-3.5 mr-1.5" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div> : <div className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-xs sm:text-sm text-foreground leading-relaxed font-sans">
                        {interpretationDecision === "reworded" && rewordedText ? rewordedText : computedInterpretation.text}
                      </p>
                    </div>

                    {/* Bullet point reflections */}
                    {interpretationDecision !== "reworded" && computedInterpretation.bullets.length > 0 && <ul className="space-y-2 pt-2 border-t border-border/50">
                          {computedInterpretation.bullets.map((bullet, idx) => <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2 leading-relaxed">
                              <span className="size-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                              <span className="flex-1">{bullet}</span>
                            </li>)}
                        </ul>}

                    {/* Disclaimer Note */}
                    <div className="p-3 rounded-lg bg-background/60 border border-border/50 text-[11px] text-muted-foreground leading-relaxed flex items-start gap-2">
                      <ShieldAlert className="size-3.5 text-primary shrink-0 mt-0.5" />
                      <span>{INTERPRETATION_DISCLAIMER}</span>
                    </div>

                    {/* Actions: This fits / Reword it / That's not me */}
                    <div className="flex flex-col min-[400px]:flex-row flex-wrap items-stretch min-[400px]:items-center gap-2.5 pt-2">
                      <button type="button" onClick={handleAccept} className={`min-h-[44px] text-xs font-serif min-w-0 break-words ${interpretationDecision === "accepted" ? "ql-btn ring-2 ring-primary" : "ql-btn"}`}>
                        <Check className="size-3.5 mr-1.5 shrink-0" />
                        <span>
                          {interpretationDecision === "accepted" ? "Accepted — This fits me" : "This fits"}
                        </span>
                      </button>

                      <button type="button" onClick={handleStartReword} className={`min-h-[44px] text-xs font-serif min-w-0 break-words ${interpretationDecision === "reworded" ? "ql-btn-ghost border-chart-4 text-chart-4 ring-1 ring-chart-4" : "ql-btn-ghost"}`}>
                        <Edit3 className="size-3.5 mr-1.5 shrink-0" />
                        <span>
                          {interpretationDecision === "reworded" ? "Edit reworded text" : "Reword it"}
                        </span>
                      </button>

                      <button type="button" onClick={handleReject} className="min-h-[44px] px-3.5 rounded-full text-xs font-mono text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors cursor-pointer inline-flex items-center justify-center gap-1.5 border border-transparent min-w-0 break-words">
                        <X className="size-3.5 shrink-0" />
                        <span>That's not me</span>
                      </button>
                    </div>
                  </div>}
              </div>)}
          </div>}
      </div>
    </section>;
}